import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-all duration-200 ease-smooth',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.97]',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]',
          'shadow-sm hover:shadow-md',
          'hover:brightness-110',
          'relative overflow-hidden',
          'after:absolute after:inset-0 after:rounded-lg',
          'after:bg-gradient-to-r after:from-transparent after:via-white/10 after:to-transparent',
          'after:translate-x-[-100%] hover:after:translate-x-[100%]',
          'after:transition-transform after:duration-500 after:ease-smooth',
        ].join(' '),
        destructive: [
          'bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)]',
          'shadow-sm hover:shadow-md',
          'hover:brightness-110',
        ].join(' '),
        outline: [
          'border border-[var(--color-border)] bg-[var(--color-background)]',
          'hover:bg-[var(--color-muted)] hover:border-[var(--color-ring)]/50',
        ].join(' '),
        ghost: 'hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]',
        link: 'text-[var(--color-primary)] underline-offset-4 hover:underline',
        secondary: [
          'bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)]',
          'shadow-sm hover:shadow-md',
          'hover:brightness-110',
        ].join(' '),
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
)
Button.displayName = 'Button'
